import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { currentTenantOrNull } from '../lib/tenants';

// Gate director-area pages on white-label tenants. Public tenant remains
// open: anyone signed in can act as a director (the existing implicit
// model where ownership of a tournament makes you that tournament's
// director). White-label tenants require app_metadata.is_director or
// is_org_admin (admins have full access by definition) — directors are
// invited, not self-registered, and we don't want a self-registered
// player to be able to navigate to /dashboard and create a tournament.
export function DirectorRoute({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('common');
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return null;

  if (!user) {
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(location.pathname)}`}
        replace
      />
    );
  }

  const tenant = currentTenantOrNull();
  if (tenant?.isWhiteLabel) {
    const meta =
      (user.app_metadata as
        | { is_director?: boolean; is_org_admin?: boolean }
        | undefined) ?? {};
    if (!meta.is_director && !meta.is_org_admin) {
      return (
        <div
          data-testid="forbidden-message"
          className="flex min-h-screen items-center justify-center bg-background p-6"
        >
          <div className="max-w-md space-y-2 text-center">
            <h1 className="text-2xl font-semibold">{t('route_guards.director_required_title')}</h1>
            <p className="text-muted-foreground">
              {t('route_guards.director_required_body')}
            </p>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
}
